/*
 * Copyright (c) 2022. Dave van Rijn Development
 */

import React, {useState} from 'react';
import {nullify, ResponseData, sendGaEvent, showDialog, validateEmail} from '@dvrd/dvr-controls';
import {ContactData, ContactErrors} from "../../util/interfaces";
import {sendContact, SendContact} from "../../util/requestSender";
import ContactForm from "./contactForm";
import {createErrorMessage} from "../../util/utils";
import {formatCitiesString} from "../../util/constants";

const emptyErrors: ContactErrors = {email: null, message: null, name: null, serviceType: null};

export default function ContactFormController() {
    const [contactData, setContactData] = useState<ContactData>({
        name: '',
        email: '',
        city: 'Vinkeveen',
        phoneNumber: '',
        message: '',
        serviceType: 'Groep',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<ContactErrors>(emptyErrors);

    function onChange(key: keyof ContactData) {
        return function(value: string) {
            onResetError(key as keyof ContactErrors)();
            if (key === 'city' && value === 'misc')
                showDialog(`Bowie's Hondenuitlaatservice haalt alleen honden op in ${formatCitiesString()}.`,
                    'Bericht van Bowie\'s Hondenuitlaatservice');
            setContactData(Object.assign({}, contactData, {[key]: value}));
        }
    }

    function onResetError(key: keyof ContactErrors) {
        return function() {
            setErrors(Object.assign({}, errors, {[key]: null}));
        }
    }

    function onClear() {
        setContactData({name: '', email: '', city: 'Vinkeveen', phoneNumber: '', message: '', serviceType: 'Groep'});
    }

    function onSubmit() {
        sendGaEvent('click', 'contact', 'submit-contact');
        if (!validateValues()) return;
        const {name, email, message, city, phoneNumber, serviceType} = contactData;
        const data: SendContact = {
            name, email, message, city, service_type: serviceType, phone_number: nullify(phoneNumber)
        };
        setLoading(true);
        sendContact({
            data, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success)
                    showDialog('Uw contactverzoek is ontvangen. Er zal zo spoedig mogelijk contact met u ' +
                        'opgenomen worden.', 'Verzoek ontvangen', [{label: 'Oke', onClick: onClear}]);
                else
                    showDialog(
                        createErrorMessage(data.message ?? 'Het verzenden van uw contactverzoek is niet gelukt.'),
                        'Verzenden mislukt')
            }
        });
    }

    function validateValues(): boolean {
        const errs = Object.assign({}, errors);
        const {name, email, message, serviceType} = contactData;
        let valid: boolean = true;
        if (!name.length) {
            errs.name = 'De naam mag niet leeg zijn';
            valid = false;
        }
        if (!email.length) {
            errs.email = 'Het e-mailadres mag niet leeg zijn';
            valid = false;
        } else if (!validateEmail(email)) {
            errs.email = 'Het e-mailadres is niet geldig';
            valid = false;
        }
        if (!message.length) {
            errs.message = 'Het bericht mag niet leeg zijn';
            valid = false;
        }
        if (!serviceType) {
            errs.serviceType = 'Kies een optie';
            valid = false;
        }
        if (!valid) setErrors(errs);
        return valid;
    }

    return (
        <ContactForm onChange={onChange} onSubmit={onSubmit} onResetError={onResetError} name={contactData.name}
                     email={contactData.email} phoneNumber={contactData.phoneNumber} message={contactData.message}
                     city={contactData.city} loading={loading} errors={errors} serviceType={contactData.serviceType}/>
    );
}